import { useRef } from 'react'

import type { GameInfo } from '../api/client'
import { useGameStore } from '../store/gameStore'

/**
 * Risk Game (gamble) panel for lines games.
 *
 * Shown only when the server has an offer on the table, or when the last
 * step's card is still worth looking at. Every number here is the
 * server's: the stake, the potential win, the card, the balance
 * (docs/lines-round-contract.md §3–4). The panel never doubles anything
 * itself — it asks, and shows what came back.
 */

const SUITS = ['♥', '♦', '♣', '♠'] as const
const RANKS = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'] as const

/** card 0..51: suit = floor(card / 13) (♥ ♦ ♣ ♠), rank = card % 13. */
function describeCard(card: number): { text: string; red: boolean } {
  const suit = Math.floor(card / 13)
  const rank = card % 13
  return { text: `${RANKS[rank] ?? '?'}${SUITS[suit] ?? '?'}`, red: suit < 2 }
}

function formatCoins(value: number): string {
  return value.toLocaleString('en-US')
}

export function GamblePanel({ game }: { game: GameInfo }): React.JSX.Element | null {
  const phase = useGameStore((s) => s.phase)
  const offer = useGameStore((s) => s.gambleOffer)
  const last = useGameStore((s) => s.lastGamble)
  const busy = useGameStore((s) => s.gambleBusy)
  const gambleDouble = useGameStore((s) => s.gambleDouble)
  const gambleCollect = useGameStore((s) => s.gambleCollect)

  const maxSteps = offer?.maxSteps ?? game.gamble?.maxSteps ?? 0
  const available = !!offer?.available && phase === 'idle'
  // A closed gamble still shows its last card until the next spin.
  const showResult = last !== null && last.card !== null
  const open = available || showResult

  const canAct = available && !busy
  const card = showResult && last.card !== null ? describeCard(last.card) : null

  // The slot stays in the DOM so the panel can GROW into place instead of
  // appearing at full size and shoving the page around. While it collapses
  // there is nothing left to render, so the last body is held one beat
  // longer — an empty box folding shut reads as a glitch, not an exit.
  const lastBody = useRef<React.JSX.Element | null>(null)

  const body = (
    <div className="gamble" role="group" aria-label="Risk Game">
      <div className="gamble__head">
        <span className="gamble__title">Risk Game</span>
        <span className="gamble__step">
          {available
            ? `step ${(offer?.step ?? 0) + 1} of ${maxSteps}`
            : last?.closed
              ? last.won === false
                ? 'lost'
                : 'collected'
              : ''}
        </span>
      </div>

      {available && offer && (
        <div className="gamble__meters">
          <div className="meter">
            <span className="meter__label">Stake</span>
            <span className="meter__value">{formatCoins(offer.stakeCoins)}</span>
          </div>
          <div className="meter">
            <span className="meter__label">Win</span>
            <span className="meter__value meter__value--win">{formatCoins(offer.potentialWinCoins)}</span>
          </div>
        </div>
      )}

      {card && last && (
        <div className={`gamble__card ${card.red ? 'gamble__card--red' : 'gamble__card--black'}`}>
          <span className="gamble__card-face">{card.text}</span>
          <span className="gamble__card-verdict">
            {last.won ? `+${formatCoins(last.payoutCoins)}` : `−${formatCoins(last.stakeCoins)}`}
          </span>
        </div>
      )}

      <div className="gamble__actions">
        <button
          className="gamble__btn gamble__btn--red"
          type="button"
          disabled={!canAct}
          onClick={() => void gambleDouble('red')}
          title="Red: ×2 or nothing"
        >
          Red
        </button>
        <button
          className="gamble__btn gamble__btn--black"
          type="button"
          disabled={!canAct}
          onClick={() => void gambleDouble('black')}
          title="Black: ×2 or nothing"
        >
          Black
        </button>
        <button
          className="gamble__btn gamble__btn--collect"
          type="button"
          disabled={!canAct}
          onClick={() => void gambleCollect()}
          title="Take the win"
        >
          {busy ? '···' : 'Collect'}
        </button>
      </div>
    </div>
  )

  if (open) lastBody.current = body

  return (
    <div
      className={`gamble-slot ${open ? 'gamble-slot--open' : ''}`}
      aria-hidden={!open}
      inert={!open}
    >
      {open ? body : lastBody.current}
    </div>
  )
}
